import { Change, SocketEvent } from '@ritabygglov/core';
import { logger } from '@sentry/react';
import { API_BASE_URL, WS_URL } from '../config';
import { ToastContext } from '../context/Toast/ToastContext';
import { uuid } from '../helpers/utility';
import { FeatureFlags, isFeatureFlagEnabled } from '../hooks/useFeatureFlags';
import { AppState } from '../types';
import { getBrowserInfo } from '../utils/browserInfo';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { IChangeBatcher } from './batching/IChangeBatcher';
import { TimeBasedBatcher } from './batching/TimeBasedBatcher';
import { FixedDelayStrategy } from './connection/FixedDelayStrategy';
import { IConnectionStrategy } from './connection/IConnectionStrategy';
import { IHeartbeatMonitor } from './heartbeat/IHeartbeatMonitor';
import { PingPongHeartbeat } from './heartbeat/PingPongHeartbeat';
import { INotificationService } from './notification/INotificationService';
import { ToastNotificationService } from './notification/ToastNotificationService';
import { IPendingChangeDrainer } from './pendingStore/IPendingChangeDrainer';
import { IPendingChangeStore } from './pendingStore/IPendingChangeStore';
import { LocalStoragePendingChangeStore } from './pendingStore/LocalStoragePendingChangeStore';
import { ThrottledPendingChangeDrainer } from './pendingStore/ThrottledPendingChangeDrainer';
import { CRDTSynchronizer } from './synchronization/CRDTSynchronizer';
import { IStateSynchronizer } from './synchronization/IStateSynchronizer';
import { IWebSocketConnection } from './websocket/IWebSocketConnection';
import { ManagedWebSocketConnection } from './websocket/ManagedWebSocketConnection';

const getWSUrl = (canvasId: string, exportToken?: string) => {
  const params = new URLSearchParams({ canvasId });
  if (exportToken) {
    params.append('token', exportToken);
  }
  return `${WS_URL}/?${params.toString()}`;
};

export type CanvasSessionParams = {
  appState: AppState;
  setAppState: React.Dispatch<React.SetStateAction<AppState>>;
  canvasId: string;
  userId?: string;
  addToast?: ToastContext['addToast'];
  onSyncStatusChange?: (pendingCount: number) => void;
  onCanvasReady?: () => void;
  onLoadProgress?: (percent: number) => void;
  reconnectDelayMs?: number;
  batchIntervalMs?: number;
  exportToken?: string;
  connectionStrategy?: IConnectionStrategy;
  changeBatcher?: IChangeBatcher<Change>;
  notificationService?: INotificationService;
  wsConnection?: IWebSocketConnection;
  heartbeatMonitor?: IHeartbeatMonitor;
  stateSynchronizer?: IStateSynchronizer;
  pendingChangeStore?: IPendingChangeStore;
  pendingChangeDrainer?: IPendingChangeDrainer;
  changesFetcher?: (url: string, options: RequestInit) => Promise<Response>;
};

type ResolvedCanvasSessionDeps = {
  canvasId: string;
  userId: string;
  addToast?: ToastContext['addToast'];
  onSyncStatusChange?: (pendingCount: number) => void;
  onCanvasReady?: () => void;
  onLoadProgress?: (percent: number) => void;
  exportToken?: string;
  connectionStrategy: IConnectionStrategy;
  changeBatcher: IChangeBatcher<Change>;
  notificationService: INotificationService;
  wsConnection: IWebSocketConnection;
  heartbeatMonitor: IHeartbeatMonitor;
  stateSynchronizer: IStateSynchronizer;
  pendingChangeStore: IPendingChangeStore;
  pendingChangeDrainer: IPendingChangeDrainer;
  changesFetcher: (url: string, options: RequestInit) => Promise<Response>;
};

export function createCanvasSession({
  setAppState,
  canvasId,
  userId = uuid(),
  addToast,
  onSyncStatusChange,
  onCanvasReady,
  onLoadProgress,
  reconnectDelayMs = 5000,
  batchIntervalMs = 500,
  exportToken,
  connectionStrategy,
  changeBatcher,
  notificationService,
  wsConnection,
  heartbeatMonitor,
  stateSynchronizer,
  pendingChangeStore,
  pendingChangeDrainer,
  changesFetcher,
}: CanvasSessionParams): CanvasSession {
  const resolvedConnectionStrategy =
    connectionStrategy ?? new FixedDelayStrategy(reconnectDelayMs);
  const resolvedChangeBatcher =
    changeBatcher ?? new TimeBasedBatcher<Change>(batchIntervalMs);
  const resolvedNotificationService =
    notificationService ?? new ToastNotificationService(addToast);
  const resolvedWsConnection =
    wsConnection ??
    new ManagedWebSocketConnection(getWSUrl(canvasId, exportToken), canvasId);
  const resolvedHeartbeatMonitor =
    heartbeatMonitor ??
    new PingPongHeartbeat(
      (msg) => resolvedWsConnection.send(msg),
      () => resolvedWsConnection.isOpen(),
      canvasId
    );
  const resolvedStateSynchronizer =
    stateSynchronizer ??
    new CRDTSynchronizer(
      setAppState,
      (changes) => resolvedChangeBatcher.add(changes),
      true
    );
  const resolvedPendingChangeStore =
    pendingChangeStore ?? new LocalStoragePendingChangeStore(canvasId);
  const resolvedPendingChangeDrainer =
    pendingChangeDrainer ?? new ThrottledPendingChangeDrainer();

  return new CanvasSession({
    canvasId,
    userId,
    addToast,
    onSyncStatusChange,
    onCanvasReady,
    onLoadProgress,
    exportToken,
    connectionStrategy: resolvedConnectionStrategy,
    changeBatcher: resolvedChangeBatcher,
    notificationService: resolvedNotificationService,
    wsConnection: resolvedWsConnection,
    heartbeatMonitor: resolvedHeartbeatMonitor,
    stateSynchronizer: resolvedStateSynchronizer,
    pendingChangeStore: resolvedPendingChangeStore,
    pendingChangeDrainer: resolvedPendingChangeDrainer,
    changesFetcher: changesFetcher ?? fetchWithRetry,
  });
}

export default class CanvasSession {
  canvasId: string;
  currentUserId: string;
  retries = 0;
  addToast?: ToastContext['addToast'];
  exportToken?: string;

  private changeBatcher: IChangeBatcher<Change>;
  private notificationService: INotificationService;
  private heartbeatMonitor: IHeartbeatMonitor;
  private stateSynchronizer: IStateSynchronizer;
  private changesFetcher: (
    url: string,
    options: RequestInit
  ) => Promise<Response>;
  private connectionStrategy: IConnectionStrategy;
  private wsConnection: IWebSocketConnection;
  private pendingChangeStore: IPendingChangeStore;
  private pendingChangeDrainer: IPendingChangeDrainer;
  private reconnectTimer?: NodeJS.Timeout;
  private onSyncStatusChange?: (pendingCount: number) => void;
  private onCanvasReady?: () => void;
  private onLoadProgress?: (percent: number) => void;

  constructor({
    canvasId,
    userId,
    addToast,
    onSyncStatusChange,
    onCanvasReady,
    onLoadProgress,
    exportToken,
    connectionStrategy,
    changeBatcher,
    notificationService,
    wsConnection,
    heartbeatMonitor,
    stateSynchronizer,
    pendingChangeStore,
    pendingChangeDrainer,
    changesFetcher,
  }: ResolvedCanvasSessionDeps) {
    this.canvasId = canvasId;
    this.currentUserId = userId;
    this.addToast = addToast;
    this.onSyncStatusChange = onSyncStatusChange;
    this.onCanvasReady = onCanvasReady;
    this.onLoadProgress = onLoadProgress;
    this.exportToken = exportToken;
    this.connectionStrategy = connectionStrategy;
    this.changeBatcher = changeBatcher;
    this.changeBatcher.onFlush((changes) => this.sendChanges(changes));
    this.notificationService = notificationService;
    this.wsConnection = wsConnection;
    this.wsConnection.onOpen(() => this.onOpen());
    this.wsConnection.onClose((ev) => this.onClose(ev));
    this.wsConnection.onError(() => this.onError());
    this.wsConnection.onMessage((ev) => this.onMessage(ev));
    this.heartbeatMonitor = heartbeatMonitor;
    this.heartbeatMonitor.onTimeout(() => {
      this.notificationService.notifyDisconnected();
      this.wsConnection.close();
      this.reConnect();
    });
    this.stateSynchronizer = stateSynchronizer;
    this.pendingChangeStore = pendingChangeStore;
    this.pendingChangeDrainer = pendingChangeDrainer;
    this.changesFetcher = changesFetcher;
    this.connect();
    this.bootstrapViaHttp();
  }

  private readonly startTime = Date.now();

  private connect() {
    this.wsConnection.connect();
  }

  private async bootstrapViaHttp(): Promise<void> {
    const LIMIT = 5000;
    let afterChangeId = 0;
    let totalCount: number | null = null;
    let fetched = 0;

    await new Promise<void>((r) => setTimeout(r, 0));
    this.onLoadProgress?.(20);

    while (true) {
      try {
        const params = new URLSearchParams({
          afterChangeId: String(afterChangeId),
          limit: String(LIMIT),
        });
        if (this.exportToken) {
          params.append('token', this.exportToken);
        }

        const url = `${API_BASE_URL}/canvases/${this.canvasId}/changes?${params}`;
        const response = await this.changesFetcher(url, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const {
          changes,
          hasMore,
          totalCount: responseTotalCount,
        } = (await response.json()) as {
          changes: Change[];
          hasMore: boolean;
          totalCount?: number;
        };

        if (responseTotalCount !== undefined) {
          totalCount = responseTotalCount;
        }

        fetched += changes.length;

        if (changes.length > 0) {
          this.stateSynchronizer.applyRemoteChanges(changes);
        }

        if (!hasMore) {
          this.onLoadProgress?.(100);
          this.onCanvasReady?.();
          break;
        }

        if (totalCount !== null && totalCount > 0) {
          const progress = Math.max(
            20,
            Math.min(Math.round((fetched / totalCount) * 100), 99)
          );
          this.onLoadProgress?.(progress);
        }

        afterChangeId = changes[changes.length - 1].changeId!;
      } catch (error) {
        logger.error('Failed to bootstrap canvas via HTTP', {
          canvasId: this.canvasId,
          afterChangeId,
          error,
        });
        this.notificationService.notifyBootstrapFailed();
        break;
      }
    }
  }

  private sendChanges(changes: Change[], batchId = uuid()) {
    if (changes.length === 0) return;
    const canvasId = this.canvasId;
    const message = {
      type: 'changes',
      batchId,
      data: changes.map((c) => ({ ...c, canvasId })),
    };

    this.pendingChangeStore.save(batchId, changes);
    this.onSyncStatusChange?.(this.pendingChangeStore.loadAll().size);

    if (this.wsConnection.isOpen()) {
      try {
        this.wsConnection.send(JSON.stringify(message));
      } catch {
        logger.error('Failed to send changes over WebSocket', {
          canvasId: this.canvasId,
          batchId,
        });
      }
    }
  }

  private async onMessage(ev: MessageEvent) {
    try {
      const raw = await ev.data.text();
      const event = JSON.parse(raw) as SocketEvent;
      if (isFeatureFlagEnabled(FeatureFlags.DebugLogging)) {
        logger.info('WebSocket message received', {
          type: event.type,
          byteLength: ev.data.size ?? raw.length,
          canvasId: this.canvasId,
        });
      }
      if (event.type === 'changes') {
        this.stateSynchronizer.applyRemoteChanges(event.data);
      } else if (event.type === 'ack') {
        this.pendingChangeStore.remove(event.batchId);
        this.onSyncStatusChange?.(this.pendingChangeStore.loadAll().size);
      } else if (event.type === 'pong') {
        this.heartbeatMonitor.resetTimeout();
      }
    } catch (_e) {
      logger.error('Error parsing incoming change on ws client', {
        type: 'WebSocketError',
        message: (_e as Error)?.message,
        canvasId: this.canvasId,
      });
    }
  }

  private reConnect() {
    if (this.reconnectTimer) {
      return;
    }

    this.heartbeatMonitor.stop();

    if (this.retries === 5) {
      logger.info('Failed to reconnect after 5 attempts', {
        message: 'Failed to reconnect to WebSocket',
        type: 'WebSocketError',
        canvasId: this.canvasId,
      });
      this.notificationService.notifyReconnecting();
    }
    this.retries++;

    const delay = this.connectionStrategy.getReconnectDelay(this.retries);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  private onClose(_ev: CloseEvent) {
    this.pendingChangeDrainer.stop();
    if (_ev.reason !== 'exit') {
      this.notificationService.notifyDisconnected();
      this.reConnect();
    }
  }

  private onError() {
    if (this.retries === 0) {
      this.notificationService.notifyReconnecting();
    }
  }

  private onOpen() {
    const pending = this.pendingChangeStore.loadAll();

    this.wsConnection.send(
      JSON.stringify({
        type: 'session_info',
        localStorage: this.pendingChangeStore.isUsingPersistentStorage(),
        pendingCount: pending.size,
        browser: getBrowserInfo(),
      })
    );

    if (pending.size > 0) {
      logger.info('Reconnecting with unacknowledged batches', {
        canvasId: this.canvasId,
        pendingBatchCount: pending.size,
      });
    }

    this.pendingChangeDrainer.start(
      this.pendingChangeStore,
      (batchId, changes) => {
        logger.info('Re-applying and re-sending unacknowledged batch', {
          canvasId: this.canvasId,
          batchId,
        });
        this.stateSynchronizer.applyRemoteChanges(changes);
        this.sendChanges(changes, batchId);
      }
    );

    if (this.changeBatcher.hasPending()) {
      logger.info('Sending pending changes upon reconnection', {
        canvasId: this.canvasId,
      });
      this.changeBatcher.flush();
    }
    if (this.retries > 0) {
      this.notificationService.notifyConnected();
    }

    this.retries = 0;
    this.connectionStrategy.reset();
    this.heartbeatMonitor.start();
  }

  getElementVersion(elementId: string) {
    return this.stateSynchronizer.getElementVersion(elementId);
  }

  addLocalChange(changes: Change[], skipSending: boolean = false) {
    if (skipSending) {
      this.stateSynchronizer.applyRemoteChanges(changes);
    } else {
      this.stateSynchronizer.applyLocalChanges(changes);
    }
  }

  cleanup() {
    const payload = JSON.stringify({
      canvasId: this.canvasId,
      pendingCount: this.pendingChangeStore.loadAll().size,
      usingPersistentStorage:
        this.pendingChangeStore.isUsingPersistentStorage(),
      sessionDurationMs: Date.now() - this.startTime,
      retries: this.retries,
      browser: getBrowserInfo(),
    });
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        `${API_BASE_URL}/diagnostics`,
        new Blob([payload], { type: 'application/json' })
      );
    }

    this.heartbeatMonitor.stop();
    this.changeBatcher.flush();
    this.pendingChangeDrainer.stop();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    this.wsConnection.cleanup();
  }
}
